/**
 * ChipInput — a multi-value text input: committed values render as removable
 * chips, the free-text tail adds on Enter.
 *
 * Net-new because nothing in @flint/ui does this — `Combobox` is scalar and
 * every existing chip in the app is display-only. Lives in the feature rather
 * than the UI package until a second consumer appears (rule 15 cuts both
 * ways: lifting a single-use component is speculation).
 *
 * Fully controlled (`value`/`onChange`), owning only the uncommitted text.
 * Dedupe is case-insensitive — "xl" after "XL" is a slip, not a new size —
 * and a duplicate flashes the existing chip instead of erroring: the value
 * the operator wanted is already there, which is not a problem to scold.
 */
import { useRef, useState } from "react";
import { X } from "lucide-react";

export interface ChipInputProps {
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Per-list cap; adding beyond it is ignored (the caller shows the limit). */
  max?: number;
  "aria-label"?: string;
}

export function ChipInput({
  value,
  onChange,
  placeholder = "Type a value, press Enter",
  max,
  "aria-label": ariaLabel,
}: ChipInputProps) {
  const [text, setText] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const clash = value.find((v) => v.toLowerCase() === trimmed.toLowerCase());
    if (clash) {
      setFlash(clash);
      setTimeout(() => setFlash(null), 600);
      setText("");
      return;
    }
    if (max !== undefined && value.length >= max) return;
    onChange([...value, trimmed]);
    setText("");
  };

  return (
    // Click anywhere focuses the tail input — the whole box reads as one field.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-sm border border-line bg-paper px-2 py-1.5 transition-colors focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100"
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((v) => (
        <span
          key={v}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
            flash === v
              ? "border-warn-100 bg-warn-50 text-amber-800"
              : "border-sky-100 bg-sky-50 text-sky-700"
          }`}
        >
          {v}
          <button
            type="button"
            aria-label={`Remove ${v}`}
            className="rounded-full p-0.5 hover:bg-sky-100"
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((x) => x !== v));
            }}
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={text}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Stop the form's submit — Enter here means "commit the chip".
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && text === "" && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        // Commit on blur too: a typed-but-unentered value silently vanishing
        // when the operator tabs away reads as data loss.
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ""}
        className="min-w-32 flex-1 bg-transparent py-0.5 text-sm text-ink outline-none placeholder:text-placeholder"
      />
    </div>
  );
}
